import React, { useState } from 'react';
import { Container, Grid, Button, Typography } from '@mui/material';
import Page from 'src/components/Page';
import { useNavigate } from 'react-router-dom';
import { TextField } from 'src/components';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BadgeImage1 } from 'src/images';
import { badgeService } from 'src/services/badge.service';
import { badgeValidationSchema } from 'src/validations/admin';
import { CreateBadgeDTO } from 'src/types/teacher.types';
import { IoImagesOutline } from 'react-icons/io5';

import '../../styles/addstudent.css';
import 'react-toastify/dist/ReactToastify.css';

export default function AddNewBadge() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [fileError, setFileError] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const mutation = useMutation({
    mutationFn: async (values: CreateBadgeDTO) => {
      try {
        // First create the badge
        const badge = await badgeService.createBadge({
          name: values.name,
          description: values.description,
          points: Number(values.points),
        });

        // If there's a selected file, upload it
        if (selectedFile) {
          await badgeService.updateBadgeImage(badge.id, selectedFile);
        }

        return badge;
      } catch (error) {
        console.error('Error creating badge:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['badges'] });
      toast.success('Badge created successfully');
      navigate('/admin/badges');
    },
    onError: (error: Error) => {
      console.error('Create badge error:', error);
      toast.error('Failed to create badge');
    },
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      description: '',
      points: '',
    },
    validationSchema: badgeValidationSchema,
    onSubmit: async (values) => {
      if (!values.name || !values.description) {
        toast.error('Please fill in all required fields');
        return;
      }

      if (!selectedFile) {
        toast.error('Please select a badge image');
        return;
      }

      try {
        await mutation.mutateAsync(values);
      } catch (error) {
        console.error('Error creating badge:', error);
      }
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileType = file.type;
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(fileType)) {
        setFileError('Only .JPG, .JPEG, or .PNG files are allowed.');
        setSelectedFile(null);
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        setFileError('File size should be less than 5MB');
        setSelectedFile(null);
        return;
      }

      try {
        setLoading(true);
        const reader = new FileReader();
        reader.onloadend = () => {
          setSelectedFile(file);
        };
        reader.readAsDataURL(file);
        setFileError('');
      } catch (error) {
        console.error('Error handling badge image:', error);
        setFileError('Failed to process image. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeletePhoto = () => {
    setSelectedFile(null);
    setFileError('');
    const fileInput = document.getElementById('_upload_pic') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <Page title="Add New Badge">
      <Container className="_container" maxWidth={false}>
        <Typography variant="h4">Add New Badge</Typography>
        <div className="_divider"></div>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={2} md={1} lg={1}>
            {selectedFile ? (
              <img
                src={URL.createObjectURL(selectedFile)}
                alt="Selected Badge"
                style={{ width: '100%', height: 100, borderRadius: '8px' }}
              />
            ) : (
              <div
                style={{
                  height: 100,
                  width: 100,
                  borderRadius: '8px',
                  // border: '1px solid #ccc',
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                }}
              >
                <IoImagesOutline size={40} />
              </div>
              // <img src={BadgeImage1} alt="Badge" style={{ width: '100%', height: 'auto' }} />
            )}
          </Grid>
          <Grid item xs={12} sm={10} md={10} lg={10}>
            <Typography variant="subtitle1" sx={{ color: 'grey.400', fontWeight: 500 }}>
              only support .JPG, .JPEG, or .PNG file.
            </Typography>

            <div className="upload_row" style={{ marginTop: 0 }}>
              <input
                type="file"
                id="_upload_pic"
                onChange={handleFileChange}
                accept="image/jpeg,image/png,image/jpg"
                required
              />
              <label className="_btn" htmlFor="_upload_pic">
                {loading ? 'Uploading...' : 'Upload  photo'}
              </label>
              {selectedFile && (
                <button
                  type="button"
                  className="_btn"
                  style={{ backgroundColor: 'var(--white)', color: 'var(--primary-main)' }}
                  onClick={handleDeletePhoto}
                >
                  Delete Photo
                </button>
              )}
            </div>
            {fileError && (
              <Typography style={{ fontSize: 12, fontWeight: 700, marginTop: 10 }} color="error">
                {fileError}
              </Typography>
            )}
          </Grid>
        </Grid>
        <br />

        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Badge Name"
                placeholder="Enter Badge Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                name="name"
                error={Boolean(formik.touched.name && formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Points"
                placeholder="Enter Points"
                type="number"
                value={formik.values.points}
                onChange={formik.handleChange}
                name="points"
                error={Boolean(formik.touched.points && formik.errors.points)}
                helperText={formik.touched.points && formik.errors.points}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                placeholder="Description"
                value={formik.values.description}
                onChange={formik.handleChange}
                name="description"
                multiline
                rows={4}
                error={Boolean(formik.touched.description && formik.errors.description)}
                helperText={formik.touched.description && formik.errors.description}
                required
              />
            </Grid>
          </Grid>
          <div className="_add_st_footer_row" style={{ marginTop: 15 }}>
            <Button
              variant="outlined"
              style={{ height: 48, color: 'var(--primary-main)', width: 120, margin: 5 }}
              onClick={() => navigate('/admin/badges')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              style={{ height: 48, width: 120, margin: 5 }}
              disabled={loading || mutation.isPending}
            >
              {loading || mutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </Container>
    </Page>
  );
}
